import { useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { Article, ArticleInfoType, IMicros } from "@tm/models"
import { useMicro } from "@tm/morpheus"
import { Box, Button, buttonClasses, Dialog, filledInputClasses, Icon, MenuItem, Paper, Select, Stack, styled, Typography } from "@tm/components"
import { CompilationShowViewModelItem, CompilationModel, useArticleMove, useCompilations, useDeleteArticles } from "@tm/data/compilations"
import { mapCompilationShowViewModelItemToArticle } from "../../../../../data/mapper"
import { isWM } from "../../../../../utils"

type Props = {
    compilationId: string
    allSelected: boolean
    selectedArticles: CompilationShowViewModelItem[]
    onSelectAll(): void
    onUnselectAll(): void
}

const SelectionPaper = styled(Paper)(() => ({
    position: "absolute",
    zIndex: 5,
    padding: "0 0 0 12px",
    marginTop: "24px",
    right: "36px",
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.54)",
    border: "1px solid #82c4f8",
    borderRadius: "3px",
}))

const StyledSelect = styled(Select)(() => ({
    [`.${filledInputClasses.input}`]: {
        padding: "5px 28px 5px 13px !important",
        borderRadius: "3px",
    },
}))

const LightTypography = styled(Typography)(() => ({
    color: "#202020",
    opacity: "0.54",
}))

const CancelButton = styled(Button)(({ theme }) => ({
    [`&.${buttonClasses.contained}`]: {
        height: "42px",
        width: "56px",
        backgroundColor: theme.palette.grey[800],
        color: "#fff",
        marginLeft: "8px",
        borderBottomLeftRadius: "0 !important",
        borderTopLeftRadius: "0 !important",
    },
}))

export function FavoriteArticleSelection({ compilationId, allSelected, selectedArticles, onSelectAll, onUnselectAll }: Props) {
    const { translateText } = useLocalization()
    const { renderMicro } = useMicro<IMicros>()
    const { deleteArticles } = useDeleteArticles(isWM())
    const { compilations } = useCompilations(isWM())
    const { moveArticle } = useArticleMove()
    const [openDialog, setOpenDialog] = useState(false)
    const basketArticles = useMemo(() => selectedArticles.map(mapCompilationShowViewModelItemToArticle) as Article[], [selectedArticles])

    function handleSelectButtonClick() {
        if (allSelected) {
            onUnselectAll()
        } else {
            onSelectAll()
        }
    }

    function handleCancelButtonClick() {
        onUnselectAll()
    }

    function handleWarningConfirm() {
        deleteArticles({ compilationId, articleIds: selectedArticles.map((article) => article.id) })
        setOpenDialog(false)
    }

    function handleMoveDropdownChange({ id }: CompilationModel) {
        if (id !== compilationId) {
            moveArticle({
                articleIds: selectedArticles.map((x) => x.id),
                sourceCompilationId: compilationId,
                targetCompilationId: id,
            })
        }
    }

    return (
        <SelectionPaper>
            <Stack direction="row" gap={1} alignItems="center">
                {/* Texts */}
                <LightTypography variant="body2">{`${translateText(701).toUpperCase()}: `}</LightTypography>
                <Typography variant="body2">
                    {selectedArticles.map((x) => x.quantity.value).reduce((sum, cur) => sum + cur)}
                    {isWM() ? selectedArticles.length && ` ${translateText(479)}` : ` ${translateText(90)}`}
                </Typography>

                {/* Select Button */}
                <Button onClick={handleSelectButtonClick}>{translateText(allSelected ? 703 : 702)}</Button>

                {/* Move Select */}
                {!isWM() && compilations && (
                    <StyledSelect
                        renderValue={(val) => (
                            <Stack direction="row" gap={1} alignItems="center">
                                <Icon name="move" />
                                {(val as CompilationModel).name}
                            </Stack>
                        )}
                        onChange={(e) => handleMoveDropdownChange(e.target.value as CompilationModel)}
                        disabled={compilations.length < 2}
                        defaultValue={{ name: translateText(13961) }}
                        MenuProps={{
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            sx: {
                                maxHeight: "400px",
                            },
                        }}
                    >
                        {compilations
                            .filter((compilation) => compilation.id !== compilationId)
                            .map((compilation) => (
                                <MenuItem key={compilation.id} value={compilation}>
                                    {compilation.name}
                                </MenuItem>
                            ))}
                    </StyledSelect>
                )}

                {/* Delete Warning Dialog */}
                <Dialog open={openDialog} skin="warning" position="top" maxWidth="lg">
                    <Stack direction="row" alignItems="center">
                        <Typography component="span" pl={2}>
                            {translateText(834)}
                        </Typography>
                        <Box pl={2} textAlign="center" display="initial">
                            <Button variant="contained" onClick={() => setOpenDialog(false)}>
                                {translateText(584)}
                            </Button>
                            <Button variant="contained" sx={{ marginLeft: 1 }} color="success" onClick={handleWarningConfirm}>
                                {translateText(585)}
                            </Button>
                        </Box>
                    </Stack>
                </Dialog>

                {/* Basket Button */}
                {renderMicro("basket", "add-to-basket", {
                    data: basketArticles,
                    articleInfoType: isWM() ? ArticleInfoType.WholesalerArticle : undefined,
                    hideQuantityField: true,
                })}

                {/* Action Buttons */}
                <Button onClick={() => setOpenDialog(true)} startIcon={<Icon name="delete" />} sx={{ marginLeft: "-4px" }} />
                <CancelButton startIcon={<Icon name="cancel-x" />} title={translateText(317)} onClick={handleCancelButtonClick} />
            </Stack>
        </SelectionPaper>
    )
}
